import { v1 as uuidV1 } from 'uuid';

import { UNASSIGNED_KEY, SHARED_KEY } from '@silverfort-nodelibs/common/utils/consts';

import { commonActionsReducer } from '../../common/actions';
import { AVAILABILITY_ZONE_FIELDS, IS_NEW_FIELD } from '../../common/fields';
import { buildField, updateTabField } from '../../common/utils';
import { maxOneEmptyTab, mustHaveWallIfDc, validateNameUnique, validateNoUnassignedDcs } from '../../common/validators';
import {
    ADD_AZ,
    CHANGE_AZ_DOMAIN,
    DELETE_AZ,
    RENAME_AZ,
    UPDATE_ENTITIES,
    MOVE_ENTITIES_TO_AZ,
    UPDATE_CONFIG
} from './availabilityZonesActions';

const { DCS, PROTECTED_DOMAINS, SF_NODES, NAME } = AVAILABILITY_ZONE_FIELDS;

export const AZ_ENTITY_TYPES = {
    WALL: 'walls',
    DC: 'dcs'
};

export const fieldsReducer = (state, action) => {
    let newState = { ...state };
    switch (action.type) {
        case UPDATE_ENTITIES:
            newState = updateEntitiesValue(newState, action);
            validateNoUnassignedDcs(newState);
            return newState;
        case MOVE_ENTITIES_TO_AZ:
            newState = moveEntitiesToAz(newState, action);
            return newState;
        case ADD_AZ:
            newState = addAz(newState);
            return newState;
        case CHANGE_AZ_DOMAIN:
            newState = changeAzDomain(newState, action);
            return newState;
        case DELETE_AZ:
            newState = deleteAz(newState, action);
            return newState;
        case RENAME_AZ:
            newState = renameAz(newState, action);
            return newState;
        case UPDATE_CONFIG:
            newState = updateConfig(newState, action);
            return newState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }, wizard = false) => {
    const { azConfig, AzConfigExtraData, DomainsConfigurations } = settings;

    if (!Object.keys(DomainsConfigurations)[0]) {
        console.error('Error: must define at least one domain before adding availability zones');
    }

    const azFields = {};
    for (const azGuid in azConfig) {
        azFields[azGuid] = initAzFields(
            azConfig[azGuid].name,
            azConfig[azGuid].dcs,
            azConfig[azGuid].walls,
            azConfig[azGuid].domains,
            azGuid
        );
    }
    azFields[SHARED_KEY] = {
        ...AzConfigExtraData,
        defaultDomain: Object.keys(DomainsConfigurations)[0],
        domainOptions: Object.keys(DomainsConfigurations).map((domainGuid) => {
            return {
                label: DomainsConfigurations[domainGuid].DomainName,
                value: domainGuid
            };
        }),
        validators: {
            validateNoUnassignedDcs: true
        }
    };

    return azFields;
};

const updateConfig = (fields, action) => {
    const { azConfig, AzConfigExtraData } = action.payload;
    const azGuidsToDelete = Object.keys(fields);
    const indexOfShared = azGuidsToDelete.indexOf(SHARED_KEY);
    if (indexOfShared !== -1) {
        azGuidsToDelete.splice(indexOfShared, 1);
    }
    const newState = { ...fields };
    for (const azGuid in azConfig) {
        if (azGuidsToDelete.indexOf(azGuid) !== -1) {
            azGuidsToDelete.splice(azGuidsToDelete.indexOf(azGuid), 1);
        }
        if (!fields[azGuid]) {
            //new az
            newState[azGuid] = initAzFields(
                azConfig[azGuid].name,
                azConfig[azGuid].dcs,
                azConfig[azGuid].walls,
                azConfig[azGuid].domain,
                azGuid
            );
        } else {
            updateTabField(newState, azGuid, NAME.ID, azConfig[azGuid].name, false);
            updateTabField(newState, azGuid, DCS.ID, azConfig[azGuid].dcs, false);
            updateTabField(newState, azGuid, SF_NODES.ID, azConfig[azGuid].walls, false);
            updateTabField(newState, azGuid, PROTECTED_DOMAINS.ID, azConfig[azGuid].domain, false);
            newState[azGuid].name = azConfig[azGuid].name;
        }
    }
    azGuidsToDelete.forEach((guidToDelete) => {
        delete newState[guidToDelete];
    });
    return {
        ...newState,
        [SHARED_KEY]: {
            ...fields.shared,
            ...AzConfigExtraData
        }
    };
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {
        azConfig: { ...currentSettings.azConfig },
        AzConfigExtraData: { ...currentSettings.AzConfigExtraData }
    };
    newSettings.AzConfigExtraData.unassigned = { ...fields.shared.unassigned };
    if (saveAll) {
        for (const guid in fields) {
            if (guid !== SHARED_KEY) {
                const azFields = fields[guid].fields;
                newSettings.azConfig[guid] = mapAzToSettings(azFields);
            }
        }
    } else {
        const azFields = fields[selectedTab].fields;
        newSettings.azConfig[selectedTab] = mapAzToSettings(azFields);
    }
    return newSettings;
};

const mapAzToSettings = (azFields) => {
    return {
        name: azFields[NAME.ID].value,
        walls: azFields[SF_NODES.ID].value,
        dcs: azFields[DCS.ID].value,
        domains: azFields[PROTECTED_DOMAINS.ID].value
    };
};

const initAzFields = (az, dcs, walls, domains, id, isNew = false) => {
    const azFields = {
        fields: {
            [NAME.ID]: buildField(NAME, az, { notWhitespace: true, required: true }),
            [PROTECTED_DOMAINS.ID]: buildField(PROTECTED_DOMAINS, domains),
            [DCS.ID]: buildField(DCS, dcs),
            [SF_NODES.ID]: buildField(SF_NODES, walls),
            [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, isNew)
        },
        validators: {
            uniqueName: { fieldId: NAME.ID, caseSensitive: false },
            mustHaveWallIfDc: { dcsFieldId: DCS.ID, wallsFieldId: SF_NODES.ID },
            maxOneEmptyTab: { fieldIds: [DCS.ID, SF_NODES.ID] }
        },
        id: id,
        name: az
    };

    return azFields;
};

const assignEntities = (fields, newValue, azGuid, type, entitiesFieldKey, oldValue) => {
    const newUnassignedValue = [...fields[SHARED_KEY][UNASSIGNED_KEY][type]];
    newValue.forEach((entity) => {
        const entityIndex = newUnassignedValue.indexOf(entity);
        if (entityIndex !== -1) {
            newUnassignedValue.splice(entityIndex, 1);
        }
    });

    // add to unassigned
    const newUnassigned = {
        ...fields[SHARED_KEY][UNASSIGNED_KEY],
        [type]: newUnassignedValue
    };

    const newState = {
        ...updateTabField(fields, azGuid, entitiesFieldKey, newValue),
        [SHARED_KEY]: {
            ...fields[SHARED_KEY],
            [UNASSIGNED_KEY]: newUnassigned
        }
    };

    maxOneEmptyTab(newState, [DCS.ID, SF_NODES.ID]);
    mustHaveWallIfDc(newState[azGuid].fields, DCS.ID, SF_NODES.ID);

    return newState;
};

const unassignEntities = (fields, newValue, azGuid, type, entitiesFieldKey, oldValue) => {
    const diff = [...oldValue];
    newValue.forEach((entity) => {
        const entityIndex = diff.indexOf(entity);
        if (entityIndex !== -1) {
            diff.splice(entityIndex, 1);
        }
    });

    // add to unassigned
    const newUnassigned = {
        ...fields[SHARED_KEY][UNASSIGNED_KEY],
        [type]: [...fields.shared[UNASSIGNED_KEY][type], ...diff]
    };

    const newState = {
        ...updateTabField(fields, azGuid, entitiesFieldKey, newValue),
        [SHARED_KEY]: {
            ...fields[SHARED_KEY],
            [UNASSIGNED_KEY]: newUnassigned
        }
    };

    maxOneEmptyTab(newState, [DCS.ID, SF_NODES.ID]);
    mustHaveWallIfDc(newState[azGuid].fields, DCS.ID, SF_NODES.ID);

    return newState;
};

const updateEntitiesValue = (fields, action) => {
    const newValue = action.payload.newValue;
    const azGuid = action.payload.azGuid;
    const type = action.payload.type;

    let entitiesFieldKey = SF_NODES.ID;
    if (type === AZ_ENTITY_TYPES.DC) {
        entitiesFieldKey = DCS.ID;
    }

    const oldValue = [...fields[azGuid].fields[entitiesFieldKey].value];
    if (oldValue.length === newValue.length) {
        return fields;
    }
    if (oldValue.length > newValue.length) {
        return unassignEntities(fields, newValue, azGuid, type, entitiesFieldKey, oldValue);
    }
    return assignEntities(fields, newValue, azGuid, type, entitiesFieldKey, oldValue);
};

const moveEntitiesToAz = (fields, action) => {
    const azGuid = action.payload.azGuid;
    const type = action.payload.type;
    const entitiesToMove = action.payload.entitiesToMove;

    if (entitiesToMove.length < 1) {
        return fields;
    }

    let entitiesFieldKey = SF_NODES.ID;
    if (type === AZ_ENTITY_TYPES.DC) {
        entitiesFieldKey = DCS.ID;
    }

    const allAzGuids = Object.keys(fields).filter((guid) => guid !== SHARED_KEY);
    let newAzFields = { ...fields };
    // remove entities from other azs
    for (const az of allAzGuids) {
        const oldValue = newAzFields[az].fields[entitiesFieldKey].value;
        const newValue = newAzFields[az].fields[entitiesFieldKey].value.filter(
            (entity) => !entitiesToMove.includes(entity)
        );
        if (newValue.length !== oldValue.length) {
            newAzFields = updateTabField(newAzFields, az, entitiesFieldKey, newValue);
        }
    }
    //add entities to az
    newAzFields = updateTabField(newAzFields, azGuid, entitiesFieldKey, [
        ...newAzFields[azGuid].fields[entitiesFieldKey].value,
        ...entitiesToMove
    ]);
    //remove entities from unassigned
    const newUnassignedValue = fields[SHARED_KEY][UNASSIGNED_KEY][type].filter(
        (entity) => !entitiesToMove.includes(entity)
    );
    const newUnassigned = { ...fields[SHARED_KEY][UNASSIGNED_KEY], [type]: newUnassignedValue };

    return {
        ...newAzFields,
        [SHARED_KEY]: {
            ...fields[SHARED_KEY],
            [UNASSIGNED_KEY]: newUnassigned
        }
    };
};

export const addAz = (fields) => {
    const guid = uuidV1();
    const newAzName = 'New AZ';
    const newState = {
        ...fields,
        [guid]: {
            ...initAzFields(newAzName, [], [], [], guid, true),
            isDirty: true,
            createdAt: Date.now()
        }
    };
    validateNameUnique(newState, guid, NAME.ID, false);
    maxOneEmptyTab(newState, [DCS.ID, SF_NODES.ID]);
    return newState;
};

const changeAzDomain = (fields, action) => {
    // will unassign all entities
    const azGuid = action.payload.azGuid;
    const newDomain = action.payload.newDomain;

    if (newDomain === fields[azGuid].fields[PROTECTED_DOMAINS.ID].value) {
        return fields;
    }
    let newState = updateTabField(fields, azGuid, PROTECTED_DOMAINS.ID, newDomain);
    newState = updateTabField(newState, azGuid, SF_NODES.ID, []);
    newState = updateTabField(newState, azGuid, DCS.ID, []);

    newState = {
        ...newState,
        [SHARED_KEY]: {
            ...newState[SHARED_KEY],
            [UNASSIGNED_KEY]: {
                ...newState[SHARED_KEY][UNASSIGNED_KEY],
                dcs: newState[SHARED_KEY][UNASSIGNED_KEY].dcs.concat(fields[azGuid].fields[DCS.ID].value),
                walls: newState[SHARED_KEY][UNASSIGNED_KEY].walls.concat(fields[azGuid].fields[SF_NODES.ID].value)
            }
        }
    };
    maxOneEmptyTab(newState, [DCS.ID, SF_NODES.ID]);
    return newState;
};

const deleteAz = (fields, action) => {
    // will unassign all entities
    const azGuid = action.payload.azGuid;
    const newAzFields = { ...fields };
    delete newAzFields[azGuid];

    const newState = {
        ...newAzFields,
        [SHARED_KEY]: {
            ...fields[SHARED_KEY],
            [UNASSIGNED_KEY]: {
                ...fields[SHARED_KEY][UNASSIGNED_KEY],
                dcs: fields[SHARED_KEY][UNASSIGNED_KEY].dcs.concat(fields[azGuid].fields[DCS.ID].value),
                walls: fields[SHARED_KEY][UNASSIGNED_KEY].walls.concat(fields[azGuid].fields[SF_NODES.ID].value)
            }
        }
    };

    validateNameUnique(newState, undefined, NAME.ID, false);
    maxOneEmptyTab(newState, [DCS.ID, SF_NODES.ID]);
    return newState;
};

const renameAz = (fields, action) => {
    const azGuid = action.payload.azGuid;
    const newAzName = action.payload.newAzName;
    const newState = updateTabField(fields, azGuid, NAME.ID, newAzName);
    newState[azGuid].name = newAzName;
    validateNameUnique(newState, azGuid, NAME.ID, false);
    return newState;
};
